import update from 'immutability-helper';
import { BroadcastReduxActions } from './Action';
import { AuthState } from 'state/authentication/Reducer';

interface BroadcastData {
    key: string;
    tabId: string;
    parentTabId: string;
    pathname: string;
    state: any;
    uuid: string;
}

export interface SocketState {
    broadcastData: BroadcastData[];
    broadcastReviewedData: BroadcastData[];
    broadcastSolicitorData: BroadcastData[];
    broadcastActiveTabId: string;
    broadcastArchive: string;
    broadcastLastMessage: string;
    broadcastProgression: boolean;
    broadcastPageLeave: boolean;
    broadcastAuthState: Pick<
        AuthState,
        'accessToken' | 'refreshToken' | 'email'
    > | null;
}

export const socketState: SocketState = {
    broadcastActiveTabId: '',
    broadcastReviewedData: [],
    broadcastSolicitorData: [],
    broadcastData: [],
    broadcastArchive: '',
    broadcastLastMessage: '',
    broadcastProgression: false,
    broadcastPageLeave: false,
    broadcastAuthState: null,
};

const BroadcastStateReducer = (
    state = socketState,
    action: any = { type: '', payload: {} }
): SocketState => {
    switch (action.type) {
        case BroadcastReduxActions.BROADCAST_CLOSE_HANDSHAKE:
            return update(state, {
                $merge: {
                    ...socketState,
                },
            });
        case BroadcastReduxActions.TO_BROADCAST_REDUX_WORKER:
            // eslint-disable-next-line no-case-declarations
            const isBroadcastKey = action.payload.tabId;
            return update(state, {
                $merge: {
                    broadcastData: Array.from(state.broadcastData).some(
                        (c) => c.tabId === action.payload.tabId
                    )
                        ? // eslint-disable-next-line consistent-return, array-callback-return
                          Array.from(state.broadcastData).map((t) => {
                              if (isBroadcastKey === t.tabId) {
                                  return {
                                      ...action.payload,
                                  };
                              }
                              return t;
                          })
                        : Array.from(state.broadcastData).concat([
                              action.payload,
                          ]),
                    broadcastReviewedData:
                        action.payload.pathname === '/single-reviewed'
                            ? Array.from(state.broadcastReviewedData).concat([
                                  action.payload,
                              ])
                            : state.broadcastReviewedData,
                    broadcastSolicitorData:
                        action.payload.pathname === '/reviewer-viewer'
                            ? Array.from(state.broadcastSolicitorData).concat([
                                  action.payload,
                              ])
                            : state.broadcastSolicitorData,
                },
            });

        case BroadcastReduxActions.TO_DRAFT_REDUX_WORKER:
            // eslint-disable-next-line no-case-declarations
            const isDraftKey = action.payload.tabId;
            return update(state, {
                $merge: {
                    broadcastData: Array.from(state.broadcastData).some(
                        (c) => c.tabId === action.payload.tabId
                    )
                        ? // eslint-disable-next-line consistent-return, array-callback-return
                          Array.from(state.broadcastData).map((t) => {
                              if (isDraftKey === t.tabId) {
                                  return {
                                      ...action.payload,
                                  };
                              }
                              return t;
                          })
                        : Array.from(state.broadcastData).concat([
                              action.payload,
                          ]),
                    broadcastReviewedData:
                        action.payload.pathname === '/single-reviewed'
                            ? Array.from(state.broadcastReviewedData).concat([
                                  action.payload,
                              ])
                            : state.broadcastReviewedData,
                    broadcastSolicitorData:
                        action.payload.pathname === '/reviewer-viewer'
                            ? Array.from(state.broadcastSolicitorData).concat([
                                  action.payload,
                              ])
                            : state.broadcastSolicitorData,
                },
            });
        case BroadcastReduxActions.BROADCAST_INITIAL_HANDSHAKE:
            return update(state, {
                $merge: {
                    broadcastArchive: action.payload,
                },
            });
        case BroadcastReduxActions.BROADCAST_REDUX_MESSAGE:
            return update(state, {
                $merge: {
                    broadcastLastMessage: action.payload,
                },
            });
        case BroadcastReduxActions.BROADCAST_REMOVE_REDUX_MESSAGE:
            return update(state, {
                $merge: {
                    broadcastData: Array.from(state.broadcastData).filter(
                        (c) =>
                            c.pathname === '/single-reviewed' &&
                            c.state &&
                            c.state.documentData &&
                            c.state.documentData.actions &&
                            c.state.documentData.actions.uuid !==
                                action.payload.state.documentData.actions.uuid
                    ),
                },
            });
        case BroadcastReduxActions.TO_REMOVE_BROADCAST_REDUX_MESSAGE:
            return update(state, {
                $merge: {
                    broadcastData: Array.from(state.broadcastData).filter(
                        (c) =>
                            c.pathname === '/single-reviewed' &&
                            c.state &&
                            c.state.documentData &&
                            c.state.documentData.actions &&
                            c.state.documentData.actions.uuid !==
                                action.payload.state.documentData.actions.uuid
                    ),
                },
            });

        case BroadcastReduxActions.BROADCAST_CLONE_REDUX_MESSAGE:
            return update(state, {
                $merge: {
                    broadcastData: action.payload,
                },
            });
        case BroadcastReduxActions.TO_CLONE_BROADCAST_REDUX_MESSAGE:
            return update(state, {
                $merge: {
                    broadcastData: action.payload,
                },
            });
        case BroadcastReduxActions.TO_BROADCAST_PROGRESSION_REDUX_MESSAGE:
            return update(state, {
                $merge: {
                    broadcastProgression: action.payload,
                },
            });
        case BroadcastReduxActions.BROADCAST_PROGRESSION_REDUX_MESSAGE:
            return update(state, {
                $merge: {
                    broadcastProgression: action.payload,
                },
            });
        case BroadcastReduxActions.BROADCAST_PAGELEAVE_REDUX_MESSAGE:
            return update(state, {
                $merge: {
                    broadcastPageLeave: action.payload,
                },
            });
        case BroadcastReduxActions.TO_BROADCAST_PAGELEAVE_REDUX_MESSAGE:
            return update(state, {
                $merge: {
                    broadcastPageLeave: action.payload,
                },
            });

        case BroadcastReduxActions.BROADCAST_AUTH_REDUX_MESSAGE:
            return update(state, {
                $merge: {
                    broadcastAuthState: action.payload,
                },
            });
        case BroadcastReduxActions.TO_BROADCAST_AUTH_REDUX_MESSAGE:
            return update(state, {
                $merge: {
                    broadcastAuthState: action.payload,
                },
            });
        case BroadcastReduxActions.BROADCAST_ACTIVE_TAB_MESSAGE:
            return update(state, {
                $merge: {
                    broadcastActiveTabId: action.payload,
                },
            });
        case BroadcastReduxActions.TO_BROADCAST_ACTIVE_TAB_MESSAGE:
            return update(state, {
                $merge: {
                    broadcastActiveTabId: action.payload,
                },
            });

        default:
            return state;
    }
};

export default BroadcastStateReducer;
